import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

interface EventRow {
  id: number;
  name: string;
  event_date: string | null;
  category_id: number | null;
  site: string | null;
  duration: number | null;
  attendee_count: number | null;
  demographics: string | null;
  notes: string | null;
}

interface EventCategory {
  id: number;
  name: string;
}

interface EventFormProps {
  event?: EventRow | null;
  onClose: () => void;
}

export function EventForm({ event, onClose }: EventFormProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [site, setSite] = useState("");
  const [duration, setDuration] = useState("");
  const [attendeeCount, setAttendeeCount] = useState("");
  const [notes, setNotes] = useState("");

  const { data: categories } = useQuery<EventCategory[]>({
    queryKey: ["event-categories"],
    queryFn: async () => {
      const res = await api.get<{ data: EventCategory[] }>("/event-categories");
      return res.data;
    },
  });

  useEffect(() => {
    if (event) {
      setName(event.name);
      setEventDate(event.event_date ?? "");
      setCategoryId(event.category_id ? String(event.category_id) : "");
      setSite(event.site ?? "");
      setDuration(event.duration != null ? String(event.duration) : "");
      setAttendeeCount(event.attendee_count != null ? String(event.attendee_count) : "");
      setNotes(event.notes ?? "");
    }
  }, [event]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post("/events", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: Record<string, unknown>) =>
      api.put(`/events/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      onClose();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data: Record<string, unknown> = {
      name,
      eventDate: eventDate || undefined,
      categoryId: categoryId ? parseInt(categoryId, 10) : undefined,
      site: site || undefined,
      duration: duration ? parseFloat(duration) : undefined,
      attendeeCount: attendeeCount ? parseInt(attendeeCount, 10) : undefined,
      notes: notes || undefined,
    };

    if (event) {
      updateMutation.mutate({ id: event.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {event ? "Edit Event" : "New Event"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label="Event Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Community Awareness Event"
            />
            <Input
              label="Event Date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-foreground">
                Category
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">No category</option>
                {(categories ?? []).map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Site / Location"
              value={site}
              onChange={(e) => setSite(e.target.value)}
              placeholder="Location name"
            />
            <Input
              label="Duration (hours)"
              type="number"
              step="0.25"
              min="0"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
            <Input
              label="Attendee Count"
              type="number"
              min="0"
              value={attendeeCount}
              onChange={(e) => setAttendeeCount(e.target.value)}
            />
          </div>
          <Input
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
          />

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(error as Error)?.message ?? "An error occurred"}
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" loading={isPending}>
              {event ? "Save Changes" : "Create Event"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
